import { isValidElement, ReactNode } from 'react';
import { convertSnakeCaseToTitleCase } from './string.utils';

/**
 * Shared helpers for the CSV/XLS export of the table components
 * (AppTable, CustomTable and FrontendSortTable).
 *
 * Table columns are allowed to reuse their `render` function for the export via
 * `exportRender: true`. Those renders often return JSX (a coloured span, a link,
 * a tag), and writing an element straight to the file leaks the whole React
 * element object into the cell. Everything that reaches the file must go
 * through `toExportValue` first.
 */

const LABEL_KEYS = ['name', 'title', 'label', 'business_name'];

/** Flattens a React node into the plain text it would render on screen. */
const extractNodeText = (node: ReactNode): string => {
  if (node === null || node === undefined || typeof node === 'boolean') {
    return '';
  }

  if (typeof node === 'string' || typeof node === 'number') {
    return `${node}`;
  }

  if (Array.isArray(node)) {
    return node.map(extractNodeText).join('');
  }

  if (isValidElement(node)) {
    return extractNodeText((node.props as { children?: ReactNode })?.children);
  }

  return '';
};

const normalizeExportValue = (value: any): string | number | boolean => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string') {
    return value.trim();
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (isValidElement(value)) {
    return extractNodeText(value).trim();
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => `${normalizeExportValue(item)}`)
      .filter((item) => item !== '')
      .join(', ');
  }

  if (typeof value === 'object') {
    const labelKey = LABEL_KEYS.find((key) => typeof value[key] === 'string' && value[key] !== '');
    return labelKey ? value[labelKey].trim() : '';
  }

  return `${value}`;
};

/**
 * Converts a rendered cell value into something safe to write to a CSV/XLS cell.
 * When the rendered value carries no readable text (a component with no text
 * children, for instance) it falls back to the raw row value so the column is
 * not silently emptied.
 */
export const toExportValue = (value: any, rawValue?: any): string | number | boolean => {
  const normalized = normalizeExportValue(value);

  if (normalized !== '') {
    return normalized;
  }

  if (rawValue !== undefined && rawValue !== value) {
    return normalizeExportValue(rawValue);
  }

  return '';
};

export const exportRenderThemeValues: Record<string, (value: any) => string> = {
  status: (value) => {
    return convertSnakeCaseToTitleCase(value);
  },
  users: (value) => {
    return value?.map((el: any) => el?.name).filter(Boolean).join(', ') ?? '';
  },
  customer: (value) => {
    return value?.business_name;
  },
  customers: (values) => {
    return values?.map((el: any) => el?.business_name).filter(Boolean).join(', ') ?? '';
  },
  array: (value) => {
    return value?.join(', ');
  },
  titleCaseArray: (value) => {
    return value?.map((el: any) => convertSnakeCaseToTitleCase(el))?.join(', ');
  },
};
